import { languageEnum } from "./global";
import { z } from "zod";

const baseUserSchema = z.object({
  name: z.string({ invalid_type_error: "Name must be a string" }).min(1, { message: "Name cannot be empty" }),
  email: z
    .string({ invalid_type_error: "Email must be a string" })
    .email({ message: "Email must be a valid email address" }),
  language: languageEnum,
});

export const createUserSchema = baseUserSchema;

export const updateUserSchema = baseUserSchema.extend({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const getUserSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const deleteUserSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const roleSchema = z.enum(["owner", "member"]);

export type ZodCreateUserSchema = z.infer<typeof createUserSchema>;
export type ZodUpdateUserSchema = z.infer<typeof updateUserSchema>;
export type ZodGetUserSchema = z.infer<typeof getUserSchema>;
export type ZodDeleteUserSchema = z.infer<typeof deleteUserSchema>;

export const userSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  language: languageEnum,
  createdAt: z.date(),
  updatedAt: z.date(),
  activeOrganizationId: z.string().nullable(),
  role: roleSchema,
});

export type UserSchema = z.infer<typeof userSchema>;

export const createUserSchemaDefaultValues: ZodCreateUserSchema = {
  name: "",
  email: "",
  language: "en",
};

export const updateOnboardingStepSchema = z.object({
  step: z.number().int().min(1).max(10),
});

export type ZodUpdateOnboardingStepSchema = z.infer<typeof updateOnboardingStepSchema>;

/**
 * User funnel status, per user (not per session). Order matters — it IS the
 * funnel order and must stay in sync with `userStatusEnum` in the DB schema:
 * signed-up → step1..step5 → paywall → trial → trial-ended → active → canceled.
 */
export const userStatusSchema = z.enum([
  "signed-up",
  "step1",
  "step2",
  "step3",
  "step4",
  "step5",
  "paywall",
  "trial",
  "trial-ended",
  "active",
  "canceled",
]);

export type UserStatusSchema = z.infer<typeof userStatusSchema>;

/**
 * POST /api/account/status — clients may only report wizard/paywall positions.
 * `signed-up` is the column default; `trial`/`active`/`canceled` are set
 * exclusively by the subscription webhooks, never by a client.
 */
export const updateUserStatusSchema = z.object({
  status: z.enum(["step1", "step2", "step3", "step4", "step5", "paywall"]),
});

export type ZodUpdateUserStatusSchema = z.infer<typeof updateUserStatusSchema>;

/**
 * POST /api/account/ad-attribution — click ids captured client-side after
 * signup (cookies the landing-page pixels set on .billingnow.com), used for
 * server-side ad conversion events. All fields optional: users who didn't
 * arrive through an ad have none of them.
 */
export const adAttributionSchema = z.object({
  fbp: z.string().max(200).optional(),
  fbc: z.string().max(500).optional(),
  gclid: z.string().max(500).optional(),
  gaClientId: z.string().max(100).optional(),
  marketingConsent: z.boolean().optional(),
});

export type ZodAdAttributionSchema = z.infer<typeof adAttributionSchema>;

/**
 * POST /api/account/password — set or change the account password.
 * `currentPassword` is only required when the user already has a chosen
 * password (users.has_password = true); the route enforces that.
 */
export const updatePasswordSchema = z.object({
  newPassword: z.string().min(8).max(128),
  currentPassword: z.string().optional(),
});

export type ZodUpdatePasswordSchema = z.infer<typeof updatePasswordSchema>;
